import { Badge } from '@calendly/ui/components/badge'
import { DownloadIcon } from '@calendly/ui/components/icons'

import { useI18n } from 'locales'

import InvoiceLineItems from './InvoiceLineItems'
import {
  InvoiceHeader,
  InvoiceHeaderImageContainer,
  InvoiceHeaderImage,
  InvoiceHeaderImagePlaceholder,
  InvoiceHeaderRight,
  InvoiceNumberLabel,
  InvoicePaidSummaryRow,
  InvoicePaidSummaryText,
  InvoicePrintExclude,
  DownloadInvoiceLink,
  DownloadInvoiceLinkText,
  InvoiceDetails,
  InvoiceDetailColumn,
  InvoiceDetailColumnRight,
  InvoiceDetailLabel,
  InvoiceDetailBodyLine,
  InvoiceDetailDateText,
  InvoiceDueDateBlock,
  InvoiceAmountDueValue,
} from './styles'

/**
 * The desktop invoice document. Rendered on desktop screens and for print on
 * every device (including phones), so printing from a phone yields the same
 * output as desktop print. The phone screen presentation is rendered
 * separately and is screen-only.
 */
const InvoiceDocument = ({
  invoice,
  headerImageSrc,
  isPaid,
  billerBusinessName,
  createdAtFormatted,
  dueAtFormatted,
  paidAtFormatted,
  amountDueValue,
  lineItems,
  currency,
  subtotalCents,
  discountTotalCents,
  discountLabel,
  taxTotalCents,
  totalCents,
  parsedTaxRate,
  taxName,
  invoiceNotes,
  isVoided,
}) => {
  const { i18n } = useI18n()

  return (
    <>
      <InvoiceHeader>
        <InvoiceHeaderImageContainer>
          {headerImageSrc ? (
            <InvoiceHeaderImage src={headerImageSrc} alt="" width={131} height={87} />
          ) : (
            <InvoiceHeaderImagePlaceholder />
          )}
        </InvoiceHeaderImageContainer>
        <InvoiceHeaderRight>
          <InvoicePrintExclude>
            <DownloadInvoiceLink
              as="button"
              type="button"
              variant="primary"
              underline={false}
              onClick={() => window.print()}
              data-testid="download-invoice-button"
            >
              <DownloadIcon />
              <DownloadInvoiceLinkText>
                {i18n.t('booking.invoice.download_invoice')}
              </DownloadInvoiceLinkText>
            </DownloadInvoiceLink>
          </InvoicePrintExclude>
          {isPaid ? (
            <InvoicePaidSummaryRow>
              <InvoicePaidSummaryText data-testid="invoice-paid-summary">
                {i18n.t('booking.invoice.paid_on_amount', {
                  amount: amountDueValue,
                  date: paidAtFormatted,
                })}
              </InvoicePaidSummaryText>
              <Badge type="success" data-testid="invoice-paid-badge">
                {i18n.t('booking.invoice.paid')}
              </Badge>
            </InvoicePaidSummaryRow>
          ) : null}
          <InvoiceNumberLabel>
            {i18n.t('booking.invoice.invoice_number', {
              invoice_number: invoice.invoice_number,
            })}
          </InvoiceNumberLabel>
        </InvoiceHeaderRight>
      </InvoiceHeader>

      <InvoiceDetails>
        <InvoiceDetailColumn>
          <InvoiceDetailLabel>
            {`${i18n.t('booking.invoice.billed_from')}:`}
          </InvoiceDetailLabel>
          {invoice?.invoice_biller_info?.name ? (
            <InvoiceDetailBodyLine>
              {invoice.invoice_biller_info.name}
            </InvoiceDetailBodyLine>
          ) : null}
          {billerBusinessName ? (
            <InvoiceDetailBodyLine>{billerBusinessName}</InvoiceDetailBodyLine>
          ) : null}
          {invoice?.invoice_biller_info?.email ? (
            <InvoiceDetailBodyLine>
              {invoice.invoice_biller_info.email}
            </InvoiceDetailBodyLine>
          ) : null}
        </InvoiceDetailColumn>

        <InvoiceDetailColumn>
          <InvoiceDetailLabel>
            {`${i18n.t('booking.invoice.billed_to')}:`}
          </InvoiceDetailLabel>
          {invoice?.contact?.full_name ? (
            <InvoiceDetailBodyLine>{invoice.contact.full_name}</InvoiceDetailBodyLine>
          ) : null}
          {invoice?.contact?.primary_email ? (
            <InvoiceDetailBodyLine>
              {invoice.contact.primary_email}
            </InvoiceDetailBodyLine>
          ) : null}
        </InvoiceDetailColumn>

        <InvoiceDetailColumn>
          <InvoiceDetailLabel>
            {`${i18n.t('booking.invoice.invoice_date')}:`}
          </InvoiceDetailLabel>
          <InvoiceDetailDateText>{createdAtFormatted}</InvoiceDetailDateText>
          <InvoiceDueDateBlock>
            <InvoiceDetailLabel>
              {`${i18n.t('booking.invoice.due_date')}:`}
            </InvoiceDetailLabel>
            <InvoiceDetailDateText>{dueAtFormatted}</InvoiceDetailDateText>
          </InvoiceDueDateBlock>
        </InvoiceDetailColumn>

        <InvoiceDetailColumnRight>
          <InvoiceDetailLabel>
            {i18n.t('booking.invoice.amount_due_with_currency', {
              currency: invoice?.invoice_settings?.currency ?? '',
            })}
          </InvoiceDetailLabel>
          <InvoiceAmountDueValue>{amountDueValue}</InvoiceAmountDueValue>
        </InvoiceDetailColumnRight>
      </InvoiceDetails>

      <InvoiceLineItems
        lineItems={lineItems}
        currency={currency}
        isPhone={false}
        subtotalCents={subtotalCents}
        discountTotalCents={discountTotalCents}
        discountLabel={discountLabel}
        taxTotalCents={taxTotalCents}
        totalCents={totalCents}
        parsedTaxRate={parsedTaxRate}
        taxName={taxName}
        invoiceNotes={invoiceNotes}
        isVoided={isVoided}
      />
    </>
  )
}

export default InvoiceDocument
